'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToastStore } from '@/stores/toast.store';
import { Toast } from './Toast';

/**
 * Global toast viewport. Mounted once in the root layout so any hook can fire a
 * toast via `useToastStore`. Sits above modals/sheets (z-[100]); the wrapper is
 * click-through (`pointer-events-none`) so toasts never block the UI underneath.
 */
export const Toaster: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismissToast = useToastStore((s) => s.dismissToast);

  // Portal target (document.body) is unavailable during SSR — render after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <div
      className="tw:fixed tw:bottom-[28px] tw:left-1/2 tw:-translate-x-1/2 tw:z-[100] tw:flex tw:w-full tw:max-w-[400px] tw:flex-col tw:items-center tw:gap-small tw:px-base tw:pointer-events-none"
      data-cy="toaster"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="tw:pointer-events-auto tw:w-full tw:flex tw:justify-center">
          <Toast {...toast} onDismiss={dismissToast} />
        </div>
      ))}
    </div>,
    document.body
  );
};

Toaster.displayName = 'Toaster';
